<template>
  <div class="chat-room-list">
    <!-- Botão Nova Conversa -->
    <div class="new-chat-btn" @click="showSearch = !showSearch">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
      </svg>
      <span>Nova Conversa</span>
    </div>

    <!-- Busca de Usuários -->
    <div v-if="showSearch" class="user-search">
      <input
        v-model="searchQuery"
        @input="searchUsers"
        placeholder="Buscar usuário..."
        class="search-input"
      />

      <div v-if="searching" class="search-loading">Buscando...</div>

      <div class="user-list" v-if="searchResults.length > 0">
        <div
          v-for="user in searchResults"
          :key="user.id"
          @click="startConversation(user)"
          class="user-item"
        >
          <div class="user-avatar">{{ user.initial }}</div>
          <div class="user-info">
            <div class="user-name">{{ user.displayName }}</div>
            <div class="user-email">{{ user.email || '-' }}</div>
          </div>
        </div>
      </div>

      <div v-if="searchQuery && searchResults.length === 0 && !searching" class="no-results">
        Nenhum usuário encontrado
      </div>
    </div>

    <!-- Lista de Conversas Existentes -->
    <div class="rooms-header" v-if="!showSearch">Conversas</div>

    <div v-if="!showSearch" class="rooms-search">
      <div class="rooms-search-box">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" class="rooms-search-icon">
          <path d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z"
                stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
        </svg>
        <input v-model="roomQuery" placeholder="Pesquisar conversas..." />
      </div>
    </div>

    <div class="room-item" v-for="room in roomsSafe" :key="room.id" @click="selectRoom(room.id)">
      <div class="room-avatar">{{ room.initial }}</div>

      <div class="room-info">
        <div class="room-row">
          <div class="room-name">{{ room.displayName }}</div>
          <div class="room-time" v-if="formatRoomTime(room)">{{ formatRoomTime(room) }}</div>
        </div>

        <div class="room-last-message">
          {{ room.lastMessage && room.lastMessage.content ? room.lastMessage.content : 'Sem mensagens' }}
        </div>
      </div>

      <span v-if="room.unreadCount" class="room-badge">{{ room.unreadCount }}</span>
    </div>

    <div v-if="roomsSafe.length === 0 && !showSearch" class="empty-state">
      Nenhuma conversa. Clique em "Nova Conversa" para começar!
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'ChatRoomList',
  data: function () {
    return {
      showSearch: false,
      searchQuery: '',
      searchResults: [],
      searching: false,
      searchTimeout: null,
      roomQuery: ''
    }
  },
  computed: {
    ...mapGetters('chatPlugin', ['rooms']),
    roomsSafe: function () {
      var list = Array.isArray(this.rooms) ? this.rooms : []
      var q = (this.roomQuery || '').trim().toLowerCase()

      function roomTs (r) {
        var ts = null
        if (r && r.lastMessage && r.lastMessage.timestamp) ts = r.lastMessage.timestamp
        else if (r && r.lastMessageTime) ts = r.lastMessageTime
        else if (r && r.updatedAt) ts = r.updatedAt
        else if (r && r.lastActivityAt) ts = r.lastActivityAt
        if (!ts) return 0
        var d = new Date(ts)
        var t = d.getTime()
        return isNaN(t) ? 0 : t
      }

      var mapped = list.map(function (r) {
        var name = (r && (r.name || r.nome)) ? String(r.name || r.nome) : 'Conversa'
        var initial = name.trim().length ? name.trim().charAt(0).toUpperCase() : '?'
        return Object.assign({}, r, {
          displayName: name,
          initial: initial,
          __ts: roomTs(r)
        })
      })

      mapped.sort(function (a, b) {
        return (b.__ts || 0) - (a.__ts || 0)
      })

      if (!q) return mapped

      return mapped.filter(function (r) {
        var name = (r.displayName || '').toLowerCase()
        var last = (r.lastMessage && r.lastMessage.content ? String(r.lastMessage.content) : '').toLowerCase()
        return name.includes(q) || last.includes(q)
      })
    }
  },
  methods: {
    selectRoom: function (roomId) {
      this.showSearch = false
      this.$emit('select', roomId)
    },

    pickData: function (resp) {
      if (Array.isArray(resp)) return resp
      if (resp && Array.isArray(resp.data)) return resp.data
      if (resp && resp.response && Array.isArray(resp.response.data)) return resp.response.data
      return []
    },

    normalizeUsers: function (users) {
      var list = Array.isArray(users) ? users : []
      return list.map(function (u) {
        var displayName = (u && (u.nome || u.name || u.username)) ? String(u.nome || u.name || u.username) : 'Usuário'
        var initial = displayName.trim().length ? displayName.trim().charAt(0).toUpperCase() : '?'
        return Object.assign({}, u, { displayName: displayName, initial: initial })
      })
    },

    getApi: function () {
      return (this.$chat && this.$chat.api)
        ? this.$chat.api
        : (this.$root && this.$root.$chat ? this.$root.$chat.api : null)
    },

    formatRoomTime: function (room) {
      var ts = null
      if (room && room.lastMessage && room.lastMessage.timestamp) ts = room.lastMessage.timestamp
      else if (room && room.lastMessageTime) ts = room.lastMessageTime
      else if (room && room.updatedAt) ts = room.updatedAt
      else if (room && room.lastActivityAt) ts = room.lastActivityAt

      if (!ts) return ''

      var d = new Date(ts)
      if (isNaN(d.getTime())) return ''

      var now = new Date()
      var today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      var dd = new Date(d.getFullYear(), d.getMonth(), d.getDate())

      if (dd.getTime() === today.getTime()) {
        return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      }

      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    },

    searchUsers: function () {
      var self = this
      clearTimeout(this.searchTimeout)

      if (!this.searchQuery || this.searchQuery.length < 2) {
        this.searchResults = []
        this.searching = false
        return
      }

      this.searching = true

      this.searchTimeout = setTimeout(function () {
        var api = self.getApi()
        if (!api || !api.searchUsers) {
          console.error('[ChatRoomList] api.searchUsers não existe')
          self.searching = false
          return
        }

        api.searchUsers(self.searchQuery)
          .then(function (resp) {
            var users = self.pickData(resp)
            self.searchResults = self.normalizeUsers(users)
            self.searching = false
          })
          .catch(function (err) {
            console.error('Erro ao buscar usuários:', err)
            self.searchResults = []
            self.searching = false
          })
      }, 300)
    },

    startConversation: function (user) {
      var self = this
      var api = this.$root.$chat.api

      api.createRoom([user.id], user.nome, false)
        .then(function (room) {
          self.$store.dispatch('chatPlugin/loadRooms')

          self.showSearch = false
          self.searchQuery = ''
          self.searchResults = []
          self.$emit('select', room.id)
        })
        .catch(function (error) {
          console.error('Erro ao criar conversa:', error)
          alert('Erro ao iniciar conversa')
        })
    }
  }
}
</script>

<style scoped>
.chat-room-list { height: 100%; overflow-y: auto; display: flex; flex-direction: column; }

.new-chat-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #9c27b0, #673ab7);
  color: white;
  cursor: pointer;
  font-weight: 600;
  border-bottom: 1px solid rgba(255,255,255,0.1);
}
.new-chat-btn:hover { background: linear-gradient(135deg, #8e24aa, #5e35b1); }

.user-search { padding: 12px 16px; border-bottom: 1px solid #eee; background: #f9f9f9; }

.search-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  outline: none;
  font-size: 14px;
}
.search-input:focus { border-color: #9c27b0; }

.search-loading { padding: 12px; text-align: center; color: #666; font-size: 13px; }

.user-list { max-height: 300px; overflow-y: auto; margin-top: 8px; }

.user-item {
  display: flex;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  border-radius: 8px;
  margin-bottom: 4px;
}
.user-item:hover { background: #f0f0f0; }

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #9c27b0, #673ab7);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 12px;
  font-size: 16px;
}

.user-info { flex: 1; min-width: 0; }
.user-name { font-weight: 600; font-size: 14px; }
.user-email { font-size: 12px; color: #666; margin-top: 2px; }

.no-results { padding: 20px; text-align: center; color: #999; font-size: 13px; }

.rooms-header {
  padding: 12px 16px;
  font-weight: 600;
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
  border-bottom: 1px solid #eee;
}

.room-item { display: flex; align-items: center; padding: 12px 16px; cursor: pointer; border-bottom: 1px solid #eee; transition: background 0.2s; }
.room-item:hover { background: #f5f5f5; }

.room-avatar { width: 48px; height: 48px; border-radius: 50%; background: linear-gradient(135deg, #9c27b0, #673ab7); color: white; display: flex; align-items: center; justify-content: center; font-size: 20px; font-weight: bold; margin-right: 12px; }

.room-info { flex: 1; min-width: 0; }
.room-row { display: flex; align-items: center; gap: 10px; }
.room-name { font-weight: 600; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.room-time { margin-left: auto; font-size: 12px; color: #888; flex-shrink: 0; }
.room-last-message { font-size: 13px; color: #666; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.room-badge { background: #9c27b0; color: white; border-radius: 10px; padding: 2px 8px; font-size: 11px; font-weight: bold; }

.empty-state { padding: 32px 16px; text-align: center; color: #999; }

.rooms-search { padding: 10px 16px; border-bottom: 1px solid #eee; background: #fff; }
.rooms-search-box { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border: 1px solid #ddd; border-radius: 10px; }
.rooms-search-box input { border: none; outline: none; width: 100%; font-size: 14px; }
.rooms-search-icon { color: #777; }
</style>



// WEBPACK FOOTER //
// ChatRoomList.vue?22caf3d5